<template>
  <div class="card main-cards">
    <el-dialog
      v-model="showExchangePopup"
      :title="
        $t('appStatisticExchangePopupTitle', {
          label: tokensConfig[Tokens.pearl].name.contract.toUpperCase(),
          pairLabel: 'USDT'
        })
      "
      modal-class="modal"
      :append-to-body="true"
      :fullscreen="isMobile"
      class="exchangeModal"
      :class="{ 'ru-locale': $i18n.locale === 'ru' }"
      destroy-on-close
    >
      <ExchangeModal
        v-if="showExchangePopup && tokenBalanceInjected"
        :token-balance="tokenBalanceInjected"
        :rate="PEARL_EXCHANGE_RATE"
        :exchange="exchangeCallback"
        :max-amount="
          BigNumber(tokenBalanceInjected)
            .minus(Number(tokenBalanceInjected) % PEARL_EXCHANGE_RATE)
            .toNumber()
        "
        :token-address="blockchain.contracts[Tokens.pearl].addresses.contract"
        :token-pair="'usdt'"
        @close-modal="showExchangePopup = false"
      />
    </el-dialog>

    <div>
      <h4 class="statistic-header">{{ $t('appStatisticTokenHeading') }}</h4>
      <div
        class="stats-cards-container"
        :class="{
          mobile: $device.isMobile
        }"
      >
        <DashboardOwned
          @open-exchange-popup="showExchangePopup = true"
          @open-purchase-tokens="() => openPurchaseTokensInjected()"
          @open-purchase-secondary-tokens="() => openPurchaseTokensInjected(true)"
        />

        <DashboardShare v-if="currentTokenInfoDataInjected?.isSellable" />

        <DashboardRewards />
      </div>

      <ClientOnly>
        <DashboardStatisticTable v-if="currentTokenInfo?.isSellable" />
      </ClientOnly>
    </div>
  </div>
</template>

<script setup lang="ts">
import { BigNumber } from 'bignumber.js';
import useEnvs from '~/composables/useEnvs';
import type { ITokenModel } from '~/types/apiService';
import { useDevice, useMainStore } from '#imports';
import { PEARL_EXCHANGE_RATE, Tokens, tokensConfig } from '~/utils/constants';
import DashboardStatisticTable from '~/components/Dashboard/DashboardStatisticTable.vue';
import { useWeb3ModalAccount } from '@web3modal/ethers/vue';

const { isMobile } = useDevice();
const { address } = useWeb3ModalAccount();
const store = useMainStore();
const { blockchain } = useEnvs();
const { exchange, showExchangePopup } = useExchange();

const { data: currentTokenInfo } = useNuxtData<ITokenModel>('dashboardCurrentTokenInfo');

const isUnstakeFinishedInjected = inject<Ref<boolean>>('isUnstakeFinished');
const tokenBalanceInjected = inject<Ref<string>>('tokenBalance');
const currentTokenInfoDataInjected = inject<Ref<ITokenModel>>('currentTokenInfoData');
const updateDataStatisticsInjected = inject<() => Promise<void>>('updateDataStatistics', async () => {});
const openPurchaseTokensInjected = inject<(isSecondary?: boolean) => Promise<void>>(
  'openPurchaseTokens',
  async () => {}
);

const exchangeCallback = async (quantity: number) => {
  if (!currentTokenInfo.value) {
    return;
  }

  await exchange(quantity, currentTokenInfo.value.name);
  await updateDataStatisticsInjected();
  store.updateVersion();
};

watch(
  [address, () => store.currentToken, isUnstakeFinishedInjected],
  async ([addressValue]) => {
    if (!addressValue) {
      return;
    }

    await updateDataStatisticsInjected();
  },
  {
    immediate: true
  }
);

onMounted(async () => {
  if (address.value) {
    await updateDataStatisticsInjected();
  }
});
</script>

<style scoped lang="scss">
.card.main-cards {
  padding: 0 16px;
}

.statistic-header {
  padding-top: 15px;
  text-align: center;
  color: var(--main-text-color);
}

.token {
  display: flex;
  align-items: center;
}

.stats-cards-container {
  display: flex;
  justify-content: space-between;
  font-family: Grenze, sans-serif;
  grid-column-gap: 16px;
  grid-row-gap: 16px;
  flex-wrap: nowrap;
  margin-bottom: 16px;

  @media screen and (max-width: 560px) {
    flex-wrap: wrap;
  }
}

.success {
  color: var(--confirm-color);
}

.failed {
  color: var(--canceled-color);
}
</style>

<style>
.stats-cards-container.mobile {
  gap: 20px;
  .el-skeleton {
    width: 100%;
  }
}
.stats-cards-container {
  .el-skeleton {
    width: calc(32.3529% + 1.6px);
    height: 370px;

    .el-skeleton__item {
      height: 370px;
      border-radius: 12px;
    }
  }
}
</style>
